// SYNC: copy of admin/service/src/constants/permissions.ts — keep 1:1
/**
 * 70 个权限点常量
 *
 * 跨 stream 接口契约 §6.4
 * 参考：project-docs/specs/2026-05-23-admin-platform-plan.md § 6.4
 *
 * admin/service/src/constants/permissions.ts 与 admin/web/src/contracts/permissions.ts 共用同一定义
 * （web 侧是逐字镜像，仅多首行 `// SYNC:`；tests/unit/contracts-mirror-sync.test.ts 守着不漂移）
 */

export const PERMISSIONS = {
  // order (3)
  ORDER_READ: 'order:read',
  ORDER_UPDATE_LOGISTICS: 'order:update_logistics',
  ORDER_REVERT_SUBSTITUTE: 'order:revert_substitute',
  // refund (3)
  REFUND_INITIATE: 'refund:initiate',
  REFUND_APPROVE: 'refund:approve',
  REFUND_EXECUTE: 'refund:execute',
  // ai_task (3)
  AI_TASK_READ: 'ai_task:read',
  AI_TASK_RETRY: 'ai_task:retry',
  AI_TASK_CANCEL: 'ai_task:cancel',
  // content (3)
  CONTENT_READ: 'content:read',
  CONTENT_AUDIT: 'content:audit',
  CONTENT_ROLLBACK: 'content:rollback',
  // prompt (3)
  PROMPT_READ: 'prompt:read',
  PROMPT_PUBLISH: 'prompt:publish',
  PROMPT_ROLLBACK: 'prompt:rollback',
  // package (3)
  PACKAGE_READ: 'package:read',
  PACKAGE_UPDATE: 'package:update',
  PACKAGE_PUBLISH: 'package:publish',
  // boost (4)
  BOOST_READ: 'boost:read',
  BOOST_READ_RELATIONSHIPS: 'boost:read_relationships',
  BOOST_FREEZE: 'boost:freeze',
  BOOST_AUDIT: 'boost:audit',
  // voucher (5)
  // VOUCHER_GRANT = 直接发券（立即生效）。VOUCHER_REQUEST / VOUCHER_APPROVE 是「申请 → 审批」
  // 两段式点名发券：运营主管只持 REQUEST（提交 / 查看自己的 / 撤回自己待审批的），
  // APPROVE（看全部申请、通过 / 驳回、处理结果不明）仅超管。
  VOUCHER_READ: 'voucher:read',
  VOUCHER_GRANT: 'voucher:grant',
  VOUCHER_REVOKE: 'voucher:revoke',
  VOUCHER_REQUEST: 'voucher:request',
  VOUCHER_APPROVE: 'voucher:approve',
  // redeem_code (1) —— 兑换码明文列表（GET /redeem-codes）。从 VOUCHER_READ 拆出：原先任何持
  // voucher:read 者（含 finance / observer）都能看到所有批次的明文未用码；生成仍挂 VOUCHER_GRANT。
  REDEEM_CODE_READ: 'redeem_code:read',
  // share (2)
  SHARE_READ: 'share:read',
  SHARE_REVOKE: 'share:revoke',
  // user (3)
  USER_READ: 'user:read',
  USER_FREEZE: 'user:freeze',
  USER_RESTORE: 'user:restore',
  // pii (4)
  PII_READ_PHONE: 'pii:read_phone',
  PII_READ_ADDRESS: 'pii:read_address',
  PII_READ_CONTENT: 'pii:read_content',
  PII_READ_L5: 'pii:read_l5',
  // config (3)
  CONFIG_READ: 'config:read',
  CONFIG_UPDATE: 'config:update',
  CONFIG_APPROVE: 'config:approve',
  // admin (4)
  ADMIN_READ: 'admin:read',
  ADMIN_CREATE: 'admin:create',
  ADMIN_DISABLE: 'admin:disable',
  ADMIN_REVOKE_SESSION: 'admin:revoke_session',
  // audit_log (1)
  AUDIT_LOG_READ: 'audit_log:read',
  // dashboard (1)
  DASHBOARD_READ: 'dashboard:read',
  // reconciliation (3)
  RECONCILIATION_READ: 'reconciliation:read',
  RECONCILIATION_UPLOAD: 'reconciliation:upload',
  RECONCILIATION_MARK: 'reconciliation:mark',
  // notification (1)
  NOTIFICATION_SEND: 'notification:send',
  // freeze (1)
  FREEZE_REVERT: 'freeze:revert',
  // lifecycle (1)
  LIFECYCLE_FORCE_PURGE_JUDICIAL: 'lifecycle:force_purge_judicial',
  // partner (5) —— 城市合伙人。原先一个「管理」点位捆了建档 / 发码 / 停止 / 补录归因，现拆三个：
  //   PARTNER_CREATE    建档 + 发合作码（POST /partners、POST /partners/:id/qrcode），
  //                     并决定能否看到合作码明文（partner-pii 的 activationCode）；给超管与运营主管
  //   PARTNER_STOP      停止合作（POST /partners/:id/stop）；给超管与运营主管
  //   PARTNER_ATTRIBUTE 人工补录归因（POST /partners/:id/attribute-manual，含 force 强制改归属）：
  //                     直接决定佣金归谁，碰钱，只给超管
  // PARTNER_READ 可给运营；COMMISSION_READ 额外开给财务。
  PARTNER_READ: 'partner:read',
  PARTNER_CREATE: 'partner:create',
  PARTNER_STOP: 'partner:stop',
  PARTNER_ATTRIBUTE: 'partner:attribute',
  COMMISSION_READ: 'commission:read',
  // settlement (2) —— 佣金结算单。SETTLEMENT_PAY（生成 / 标记打款）只给超管与财务：
  // 这是真正把钱安排出去的动作。
  SETTLEMENT_READ: 'settlement:read',
  SETTLEMENT_PAY: 'settlement:pay',
  // ticket (2) —— 工单。原先读写都挂 USER_READ，只读角色（observer）持 USER_READ
  // 就能派单 / 推进状态，且详情含用户联系方式；故单拆两点，observer 都不给。
  TICKET_READ: 'ticket:read',
  TICKET_HANDLE: 'ticket:handle',
  // advisor (8) —— 顾问陪跑交付（销售带客户从付款走到收书）。
  // 除 ADVISOR_MANAGE 外，持有者只能碰「名下客户」：范围由服务端按
  // advisor_admin_id 强制过滤（middleware/advisor-scope.ts），不靠这里的点位表达。
  // 因此 sales 不需要也不得持有 order:* / user:read / pii:* 这类全局点位——
  // 那会让销售看到全部用户。ADVISOR_MANAGE（看全部客户 / 转交 / 销售管理 /
  // 金额区间 / 交叉佣金判定）只给超管。
  ADVISOR_CUSTOMER_READ: 'advisor:customer_read',
  ADVISOR_CUSTOMER_WRITE: 'advisor:customer_write',
  ADVISOR_ORDER_CREATE: 'advisor:order_create',
  ADVISOR_FULFILL: 'advisor:fulfill',
  ADVISOR_CONTENT_READ: 'advisor:content_read',
  ADVISOR_MANAGE: 'advisor:manage',
  // 团队概览：各销售名下客户数 / 阶段 / 成交 + 不含联系方式与内容的团队客户名册（供选人转交）。
  // 刻意不经过 advisor-scope 放大成「全部客户详情」——它不是 ADVISOR_MANAGE 的子集。
  ADVISOR_TEAM_VIEW: 'advisor:team_view',
  // 转交客户（受约束：理由必填、每次 ≤20 位、24h 累计 ≤50 位、落审计）。
  // ADVISOR_MANAGE 仍可无约束转交，并独占「一键转交全部」。
  ADVISOR_TRANSFER: 'advisor:transfer',
  // sales_account (1) —— 销售账号管理：创建 / 查看 / 绑企微 / 停用 / 启用 role=sales 的账号。
  // 只能碰 sales 账号、不得改角色，所以能安全下放给运营主管；通用的 ADMIN_CREATE 能建超管、
  // 能把任何人改成超管，不能给。
  SALES_ACCOUNT_MANAGE: 'sales_account:manage',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];
